/**
 * SCI Field QC App — API route contract.
 *
 * The routes agent registers exactly these routes. The frontend agent calls
 * exactly these routes, building URLs with the `apiPath` helpers below.
 * Nothing may call a path that does not appear here.
 *
 * Access control
 * --------------
 *   'public' — no session required (POST /api/auth/login and GET /api/session).
 *   'any'    — a valid crew OR client session. The field QC read-only surface.
 *              An OFFICE session does not satisfy it: an account login is
 *              not a field QC account and must not read a pour.
 *   'crew'   — crew session ONLY. 403 for a client or office session, 401 for a
 *              revoked or absent share token.
 *   'office' — office session ONLY. 403 for a crew or client session. A route
 *              may additionally declare `requiresArea`: an office session only
 *              satisfies it when the session's own `area` matches, so an
 *              account for one area (e.g. mix designs) cannot reach another
 *              area's routes once a second area exists.
 *
 * Every one of these is enforced server-side, never merely hidden in the UI.
 *
 * Request / response types for each route live in ./types.ts; the type names
 * are named in each route's `request` / `response` fields so both agents bind
 * to the same shapes.
 */

import type {
  AccountArea,
  Role
} from './types';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

/** Access level a route demands. */
export type RequiredRole = 'public' | 'any' | 'crew' | 'office';

export interface RouteDef {
  method: HttpMethod;
  /** Path with :params, exactly as registered in Hono. */
  path: string;
  requiresRole: RequiredRole;
  /**
   * Which area an office session must belong to, for routes that need one.
   * Only meaningful alongside `requiresRole: 'office'`. Absent means "any
   * office session" — true today because only one area exists, but a second
   * area must set this or it will reach routes it should not. A list means
   * any of those areas: the site routes serve quality and admin.
   */
  requiresArea?: AccountArea | readonly AccountArea[];
  /** Interface name in ./types.ts, or null for an empty body. */
  request: string | null;
  /**
   * Interface name in ./types.ts, or a MIME type for file downloads.
   * `MaterialOptionsResponse` is declared in this file — see the note there.
   */
  response: string;
  description: string;
}

/** The areas a route serves, as a list — one, several, or none. */
export function routeAreas(def: RouteDef): AccountArea[] {
  if (def.requiresArea === undefined) return [];
  return Array.isArray(def.requiresArea) ? [...def.requiresArea] : [def.requiresArea as AccountArea];
}

/** Name of the HttpOnly signed session cookie. */
export const SESSION_COOKIE_NAME = 'sci_session';

/** Session lifetime in seconds (12 h — a shift plus overrun). */
export const SESSION_TTL_SECONDS = 12 * 60 * 60;

/** Login attempts allowed per project code per window, before 429. */
export const LOGIN_RATE_LIMIT = { attempts: 10, window_seconds: 300 } as const;

/** Frontend route (not an API route) that a share link points at. */
export const SHARE_LINK_PATH_PATTERN = '/share/:code/:token';

/**
 * The priced materials as the quality area sees them: names to pick, no cost.
 *
 * Declared here rather than in ./types.ts because the whole point of the
 * shape is what it leaves out — a Daily Report delivery names a material, and
 * a quality login must never be able to read the price of one off a dropdown.
 * Keeping it beside the route that serves it keeps that constraint visible.
 */
export interface MaterialOptionsResponse {
  materials: string[];
}

/**
 * The active roster as the quality area sees it: names to pick, no rate.
 *
 * Declared here for the same reason as MaterialOptionsResponse above — the
 * whole point of the shape is what it leaves out. A Daily Report names a
 * person; a quality login must never be able to read what that person is paid
 * off a dropdown. Keeping it beside the route that serves it keeps that
 * constraint visible.
 */
export interface PersonnelOptionsResponse {
  names: string[];
}

/**
 * The Other activities as the quality area sees them: a name and the unit
 * "how many" is counted in (hour, load, each) — never the rate. Same reason
 * as its neighbours for living here.
 */
export interface ActivityOptionsResponse {
  activities: { name: string; unit: string }[];
}

/**
 * Equipment that carries a charge rate, as the quality area sees it: names to
 * pick, no rate. Declared here for the same reason as its two neighbours —
 * the point of the shape is what it leaves out.
 */
export interface EquipmentOptionsResponse {
  equipment: string[];
}

/** Every route in the application. */
export const API_ROUTES = {
  /* ---------------------------------------------------------------- auth */
  login: {
    method: 'POST',
    path: '/api/auth/login',
    requiresRole: 'public',
    request: 'LoginRequest',
    response: 'LoginResponse',
    description:
      'Project code + crew or client password. Sets the HttpOnly signed session cookie. Rate limited per project code.'
  },
  shareLogin: {
    method: 'POST',
    path: '/api/auth/share-login',
    requiresRole: 'public',
    request: 'ShareLoginRequest',
    response: 'LoginResponse',
    description:
      'Arrival via /share/<code>/<token>. Always yields a client session. Rejects a revoked or unknown token with 401.'
  },
  logout: {
    method: 'POST',
    path: '/api/auth/logout',
    requiresRole: 'public',
    request: null,
    response: 'OkResponse',
    description:
      'Clears the session cookie. Public because it protects nothing — and because it must work for every role, including office, which does not satisfy any other level.'
  },
  session: {
    method: 'GET',
    path: '/api/session',
    requiresRole: 'public',
    request: null,
    response: 'SessionResponse',
    description:
      'Current session, or { session: null, project: null } when signed out. Never 401s — the frontend uses it to decide whether to show the login screen.'
  },
  setShift: {
    method: 'POST',
    path: '/api/session/shift',
    requiresRole: 'any',
    request: 'SetShiftRequest',
    response: 'LoginResponse',
    description:
      'Shift picker. Sets Session.shiftName, which stamps every subsequent entry and audit row. Re-issues the cookie.'
  },

  /* --------------------------------------------------------- mix designs */
  listMixDesigns: {
    method: 'GET',
    path: '/api/mix-designs',
    requiresRole: 'office',
    requiresArea: ['mix_designs', 'admin'],
    request: null,
    response: 'MixDesignListResponse',
    description: 'Saved mix designs, most recently updated first.'
  },
  createMixDesign: {
    method: 'POST',
    path: '/api/mix-designs',
    requiresRole: 'office',
    requiresArea: ['mix_designs', 'admin'],
    request: 'SaveMixDesignRequest',
    response: 'MixDesignResponse',
    description:
      'Creates a design and its material rows in one transaction. With no materials supplied, the standard seven rows of the SCI sheet are seeded.'
  },
  getMixDesign: {
    method: 'GET',
    path: '/api/mix-designs/:mixId',
    requiresRole: 'office',
    requiresArea: ['mix_designs', 'admin'],
    request: null,
    response: 'MixDesignResponse',
    description: 'One design with its material rows, in seq order.'
  },
  updateMixDesign: {
    method: 'PATCH',
    path: '/api/mix-designs/:mixId',
    requiresRole: 'office',
    requiresArea: ['mix_designs', 'admin'],
    request: 'SaveMixDesignRequest',
    response: 'MixDesignResponse',
    description:
      'Saves the whole document. When `materials` is present the rows are REPLACED transactionally; when absent they are left alone.'
  },
  deleteMixDesign: {
    method: 'DELETE',
    path: '/api/mix-designs/:mixId',
    requiresRole: 'office',
    requiresArea: ['mix_designs', 'admin'],
    request: null,
    response: 'OkResponse',
    description: 'Deletes the design and, by cascade, its material rows.'
  },

  /* --------------------------------------------------------- quality: JHA */
  listJha: {
    method: 'GET',
    path: '/api/quality/jha',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'JhaListResponse',
    description: 'Saved Job Hazard Analyses, most recently updated first.'
  },
  createJha: {
    method: 'POST',
    path: '/api/quality/jha',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveJhaRequest',
    response: 'JhaResponse',
    description:
      "Creates a JHA and its step rows in one transaction. With no steps supplied, the record starts with none — the paper's blank rows are just space, not content."
  },
  getJha: {
    method: 'GET',
    path: '/api/quality/jha/:jhaId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'JhaResponse',
    description: 'One JHA with its step rows, in seq order.'
  },
  updateJha: {
    method: 'PATCH',
    path: '/api/quality/jha/:jhaId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveJhaRequest',
    response: 'JhaResponse',
    description:
      'Saves the whole document. When `steps` is present the rows are REPLACED transactionally; when absent they are left alone.'
  },
  deleteJha: {
    method: 'DELETE',
    path: '/api/quality/jha/:jhaId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OkResponse',
    description: 'Admin only — the quality login cannot delete a record. Deletes the JHA and, by cascade, its step rows.'
  },

  /* ----------------------------------------------- quality: field tests */
  listFieldTests: {
    method: 'GET',
    path: '/api/quality/field-tests',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'FieldTestLogListResponse',
    description: 'Saved Concrete Field Testing logs, most recently updated first.'
  },
  createFieldTest: {
    method: 'POST',
    path: '/api/quality/field-tests',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveFieldTestLogRequest',
    response: 'FieldTestLogResponse',
    description:
      "Creates a log and its rows in one transaction. With no rows supplied, the log starts with none — the paper's blank rows are just space, not content."
  },
  getFieldTest: {
    method: 'GET',
    path: '/api/quality/field-tests/:fieldTestId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'FieldTestLogResponse',
    description: 'One log with its rows, in seq order.'
  },
  updateFieldTest: {
    method: 'PATCH',
    path: '/api/quality/field-tests/:fieldTestId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveFieldTestLogRequest',
    response: 'FieldTestLogResponse',
    description:
      'Saves the whole document. When `rows` is present the rows are REPLACED transactionally; when absent they are left alone.'
  },
  deleteFieldTest: {
    method: 'DELETE',
    path: '/api/quality/field-tests/:fieldTestId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OkResponse',
    description: 'Admin only — the quality login cannot delete a record. Deletes the log and, by cascade, its rows.'
  },

  /* -------------------------------------------------- quality: equipment */
  listEquipment: {
    method: 'GET',
    path: '/api/quality/equipment',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'EquipmentInspectionListResponse',
    description: 'Saved Equipment Safety Inspection & Repair Reports, most recently updated first.'
  },
  createEquipment: {
    method: 'POST',
    path: '/api/quality/equipment',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveEquipmentInspectionRequest',
    response: 'EquipmentInspectionResponse',
    description:
      'Creates an inspection and its checklist rows in one transaction. With no items supplied, the full printed checklist is seeded, status `pending`.'
  },
  getEquipment: {
    method: 'GET',
    path: '/api/quality/equipment/:equipmentId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'EquipmentInspectionResponse',
    description: 'One inspection with its checklist rows, in seq order.'
  },
  updateEquipment: {
    method: 'PATCH',
    path: '/api/quality/equipment/:equipmentId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveEquipmentInspectionRequest',
    response: 'EquipmentInspectionResponse',
    description:
      'Saves the whole document. When `items` is present the rows are REPLACED transactionally; when absent they are left alone.'
  },
  deleteEquipment: {
    method: 'DELETE',
    path: '/api/quality/equipment/:equipmentId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OkResponse',
    description: 'Admin only — the quality login cannot delete a record. Deletes the inspection and, by cascade, its checklist rows.'
  },

  /* --------------------------------------------- quality: daily reports */
  listDailyReports: {
    method: 'GET',
    path: '/api/quality/daily-reports',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'DailyReportListResponse',
    description: 'Saved Daily Reports, most recently updated first.'
  },
  createDailyReport: {
    method: 'POST',
    path: '/api/quality/daily-reports',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveDailyReportRequest',
    response: 'DailyReportResponse',
    description:
      'Creates a report and its personnel, delivery and activity rows in one transaction. With no personnel or deliveries supplied, the documented defaults are seeded; activities start empty either way.'
  },
  getDailyReport: {
    method: 'GET',
    path: '/api/quality/daily-reports/:dailyReportId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'DailyReportResponse',
    description: 'One report with its personnel, delivery and activity rows, each in seq order.'
  },
  updateDailyReport: {
    method: 'PATCH',
    path: '/api/quality/daily-reports/:dailyReportId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveDailyReportRequest',
    response: 'DailyReportResponse',
    description:
      'Saves the whole document. `personnel`, `deliveries` and `activities` are each independently three-state: when present the rows are REPLACED transactionally; when absent they are left alone.'
  },
  deleteDailyReport: {
    method: 'DELETE',
    path: '/api/quality/daily-reports/:dailyReportId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OkResponse',
    description: 'Admin only — the quality login cannot delete a record. Deletes the report and, by cascade, its personnel, delivery, activity and photo rows.'
  },

  /* ---------------------------------------- quality: daily report photos */
  listDailyReportPhotos: {
    method: 'GET',
    path: '/api/quality/daily-reports/:dailyReportId/photos',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'DailyReportPhotoListResponse',
    description: 'Pictures attached to a Daily Report, newest first.'
  },
  uploadDailyReportPhoto: {
    method: 'POST',
    path: '/api/quality/daily-reports/:dailyReportId/photos',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    // Raw image bytes, not JSON: the body IS the downscaled picture, with its
    // Content-Type header saying which image type. The one non-JSON request
    // body in the contract, for the same reason the pour pipeline's PUT is
    // outside it entirely — a photo is bytes, not fields.
    request: null,
    response: 'DailyReportPhotoResponse',
    description:
      'Uploads one picture (raw image bytes in the body; JPEG, PNG or WebP; downscaled on-device first) into R2 and attaches it to the report in one request. Idempotent on content: the same bytes sent twice return the existing photo row.'
  },
  deleteDailyReportPhoto: {
    method: 'DELETE',
    path: '/api/quality/daily-report-photos/:photoId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'OkResponse',
    description:
      'Removes one attached picture — row and stored object both. Open to the quality login the way editing any other part of the document is: a photo row is a row of the report, not a record of its own.'
  },
  getDailyReportPhotoContent: {
    method: 'GET',
    path: '/api/quality/daily-report-photos/:photoId/content',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'image/jpeg',
    description: 'Streams the stored image. The only way the browser reads a Daily Report picture; R2 is never public.'
  },

  /* ------------------------------------------ quality: contract reports */
  listContractReports: {
    method: 'GET',
    path: '/api/quality/contract-reports',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'ContractReportListResponse',
    description: 'Saved Contract Reports, most recently updated first.'
  },
  createContractReport: {
    method: 'POST',
    path: '/api/quality/contract-reports',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveContractReportRequest',
    response: 'ContractReportResponse',
    description:
      'Creates a report and its equipment, operator, activity and hire rows in one transaction. Every child list starts empty when not supplied — a contract day has no default equipment or crew. A hire\'s cost_per_day is admin only: a non-admin session may send it without error, but it has no effect.'
  },
  getContractReport: {
    method: 'GET',
    path: '/api/quality/contract-reports/:contractReportId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'ContractReportResponse',
    description: 'One report with its equipment, operator, activity and hire rows, each in seq order. A hire\'s cost_per_day is stripped for a non-admin session.'
  },
  updateContractReport: {
    method: 'PATCH',
    path: '/api/quality/contract-reports/:contractReportId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveContractReportRequest',
    response: 'ContractReportResponse',
    description:
      'Saves the whole document. `equipment`, `operators`, `activities` and `hires` are each independently three-state: when present the rows are replaced transactionally; when absent they are left alone. `operators`/`activities` replace their rows wholesale; `equipment`/`hires` reconcile instead — a row is matched to a stored one by its `id`, or, when it carries none, by its folded name and dates; anything matching neither is inserted, and a stored row nothing matched is deleted — a column an incoming equipment row does not mention (start_date/end_date) is left untouched rather than blanked, and a hire\'s cost_per_day is ignored on write from a non-admin session, an explicit null/blank counting as "not sent" even from admin. A non-admin save that would drop a stored hire carrying a non-zero cost without naming it by `id` is refused with a 422 — reload the card and resend the rows with their ids.'
  },
  deleteContractReport: {
    method: 'DELETE',
    path: '/api/quality/contract-reports/:contractReportId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OkResponse',
    description: 'Admin only — the quality login cannot delete a record. Deletes the report and, by cascade, its equipment, operator, activity and hire rows.'
  },

  /* ---------------------------------------- quality: equipment options */

  qualityEquipmentOptions: {
    method: 'GET',
    path: '/api/quality/equipment-options',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'EquipmentOptionsResponse',
    description:
      'Every piece of equipment that carries a charge rate and has not been archived, as a pick-list of names. The quality area never sees a rate.'
  },

  /* ---------------------------------------- quality: mix design options */

  qualityMixDesignOptions: {
    method: 'GET',
    path: '/api/quality/mix-design-options',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'MixDesignOptionsResponse',
    description:
      'The mix design library as a pick-list — id, code and project only. The quality area never sees a price.'
  },

  /* ---------------------------------------- quality: personnel options */

  qualityPersonnelOptions: {
    method: 'GET',
    path: '/api/quality/personnel-options',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'PersonnelOptionsResponse',
    description:
      'Every worker who has a rate and has not been archived, as a pick-list of names. The quality area never sees a rate.'
  },

  /* ----------------------------------------- quality: material options */

  qualityMaterialOptions: {
    method: 'GET',
    path: '/api/quality/material-options',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'MaterialOptionsResponse',
    description:
      'Every material that carries a price, as a pick-list of names. The quality area never sees a price.'
  },

  /* ----------------------------------------- quality: activity options */

  qualityActivityOptions: {
    method: 'GET',
    path: '/api/quality/activity-options',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'ActivityOptionsResponse',
    description:
      'The Other activities pick-list (Standby, Delivery, Small load fee, plus any the admin has rated), with units. ?site= adds that site\'s own. Never a rate.'
  },

  /* ------------------------------------------------- job sites (shared) */

  listSites: {
    method: 'GET',
    path: '/api/sites',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'SiteListResponse',
    description: 'Every job site with its form counts, active first then by name.'
  },
  createSite: {
    method: 'POST',
    path: '/api/sites',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveSiteRequest',
    response: 'JobSite',
    description: 'A new site. Name is required and unique case-insensitively (409 on a duplicate).'
  },
  getSite: {
    method: 'GET',
    path: '/api/sites/:siteId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'SiteResponse',
    description: 'One site and the five lists of forms filed under it.'
  },
  updateSite: {
    method: 'PATCH',
    path: '/api/sites/:siteId',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveSiteRequest',
    response: 'JobSite',
    description: 'Edits the site. Renaming writes the new name into every linked form’s typed job column.'
  },
  deleteSite: {
    method: 'DELETE',
    path: '/api/sites/:siteId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OkResponse',
    description: 'Admin only. Refused (409) while any form references the site.'
  },

  /* ------------------------------------------- cement inventory (quality) */

  getCementInventory: {
    method: 'GET',
    path: '/api/quality/cement',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: null,
    response: 'CementInventoryResponse',
    description: 'A site’s cement: deliveries in off its Daily Reports, cement out off every pour (m3 by its mix design cement per m3), the running balance, and the stock counts. ?site= is required.'
  },
  createCementCount: {
    method: 'POST',
    path: '/api/quality/cement/counts',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'SaveCementCountRequest',
    response: 'CementInventoryResponse',
    description: 'Records what was in the silo at the end of a day. Answers with the whole inventory again.'
  },
  archiveCementCount: {
    method: 'POST',
    path: '/api/quality/cement/counts/archive',
    requiresRole: 'office',
    requiresArea: ['quality', 'admin'],
    request: 'ArchiveCementCountRequest',
    response: 'CementInventoryResponse',
    description: 'Removes a stock count from the ledger, or restores it. Never deletes.'
  },

  /* -------------------------------------------------------- admin: P&L */

  adminPnl: {
    method: 'GET',
    path: '/api/admin/pnl',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'PnlDashboardResponse',
    description: 'Revenue, cost and margin for ?day=YYYY-MM-DD, ?week=YYYY-MM-DD (any date in the Monday-to-Sunday week meant) or ?month=YYYY-MM, total and per project.'
  },
  adminPnlProject: {
    method: 'GET',
    path: '/api/admin/pnl/project',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'PnlBreakdownResponse',
    description: 'One project (?name=) in one scope: every labour, material and pour line.'
  },
  adminPnlMonths: {
    method: 'GET',
    path: '/api/admin/pnl/months',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'TrackedMonthsResponse',
    description:
      'Revenue, cost and margin for count months (1-24, default 12) ending at ?to=YYYY-MM, zero-filled, optionally narrowed to ?site=. Feeds the Tracked chart — a run of months in one request, where /admin/pnl answers for a single period.'
  },
  adminSites: {
    method: 'GET',
    path: '/api/admin/sites',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'AdminSitesResponse',
    description: 'Every site with its all-time revenue, cost, margin, days worked and margin per day.'
  },
  adminBreakdown: {
    method: 'GET',
    path: '/api/admin/breakdown',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'BreakdownResponse',
    description:
      'Actual dollars in and out from imported bank statements, for ?month=YYYY-MM or ?year=YYYY, optionally narrowed to ?site=. Carries the rollups and the ledger lines behind them on one response, so a table and the charts above it cannot scope themselves differently. Never derived from Daily Reports.'
  },

  /* ----------------------------------------------------- admin: prices */

  adminRateGaps: {
    method: 'GET',
    path: '/api/admin/prices/gaps',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'RateGapsResponse',
    description:
      'Names and materials seen on Daily Reports that carry no rate, with how often and how recently each appeared. Answers the Rates screen and the dashboard chip from one query so the two cannot disagree.'
  },
  adminDismissName: {
    method: 'POST',
    path: '/api/admin/prices/dismiss',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'DismissNameRequest',
    response: 'RateGapsResponse',
    description:
      'Stop asking for a rate for one name, or resume. Hides it from the Rates gap list and the dashboard count; changes no Daily Report and no figure in the P&L. Returns the gap lists as they now stand.'
  },
  listMaterialPrices: {
    method: 'GET',
    path: '/api/admin/prices/materials',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'MaterialPriceListResponse',
    description: 'Every material price row, newest effective date first.'
  },
  createMaterialPrice: {
    method: 'POST',
    path: '/api/admin/prices/materials',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'CreateMaterialPriceRequest',
    response: 'MaterialPrice',
    description: 'A new price row. Prices are never edited; a change is a new row with its own effective date.'
  },
  archiveMaterial: {
    method: 'POST',
    path: '/api/admin/prices/materials/archive',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'ArchiveMaterialRequest',
    response: 'MaterialPriceListResponse',
    description:
      'Removes a material from the Rates list, or restores it. Every price row of that material is kept: past days still price. Returns the full list.'
  },
  listActivityRates: {
    method: 'GET',
    path: '/api/admin/prices/activities',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'ActivityRateListResponse',
    description: 'Every activity rate row (Standby, Delivery, Small load fee, ...), by activity, default before site, newest first.'
  },
  createActivityRate: {
    method: 'POST',
    path: '/api/admin/prices/activities',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'CreateActivityRateRequest',
    response: 'ActivityRate',
    description: 'A new billed rate for an Other activity, for one job site or (no site_id) the default for any site without its own.'
  },
  archiveActivity: {
    method: 'POST',
    path: '/api/admin/prices/activities/archive',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'ArchiveActivityRequest',
    response: 'ActivityRateListResponse',
    description:
      'Removes an activity from one price list (the default, or one site\'s), or restores it. Every row is kept: past days still bill. Returns the full list.'
  },
  listWorkerRates: {
    method: 'GET',
    path: '/api/admin/prices/workers',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'WorkerRateListResponse',
    description: 'Every worker rate row, newest effective date first.'
  },
  createWorkerRate: {
    method: 'POST',
    path: '/api/admin/prices/workers',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'CreateWorkerRateRequest',
    response: 'WorkerRate',
    description: 'A new hourly rate row for a worker, effective from a date.'
  },
  archiveWorker: {
    method: 'POST',
    path: '/api/admin/prices/workers/archive',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'ArchiveWorkerRequest',
    response: 'WorkerRateListResponse',
    description:
      'Removes a worker from the Rates list, or restores them. Every rate row of that name is kept: past days still price. Returns the full list.'
  },
  listMixPrices: {
    method: 'GET',
    path: '/api/admin/prices/mixes',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'MixPriceListResponse',
    description: 'Every mix design in the library with its sell prices, priced or not.'
  },
  createMixPrice: {
    method: 'POST',
    path: '/api/admin/prices/mixes',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'CreateMixPriceRequest',
    response: 'MixDesignPrice',
    description: 'A new sell price per m³ for a mix design, effective from a date.'
  },
  archiveMix: {
    method: 'POST',
    path: '/api/admin/prices/mixes/archive',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'ArchiveMixRequest',
    response: 'MixPriceListResponse',
    description:
      'Removes a mix design from the Rates list, or restores it. Every price row of that mix design is kept: past days still price. Returns the full list.'
  },
  listTruckingRates: {
    method: 'GET',
    path: '/api/admin/prices/trucking',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'TruckingRateListResponse',
    description: 'Every trucking rate row, the default rate first, newest effective date first within a site.'
  },
  createTruckingRate: {
    method: 'POST',
    path: '/api/admin/prices/trucking',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'CreateTruckingRateRequest',
    response: 'TruckingRate',
    description: 'A new $/tonne freight rate, for one job site or (with no site_id) for any site without its own.'
  },
  archiveTrucking: {
    method: 'POST',
    path: '/api/admin/prices/trucking/archive',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'ArchiveTruckingRequest',
    response: 'TruckingRateListResponse',
    description:
      'Removes a trucking rate from the Rates list, or restores it, keyed by site_id — absent or null for the default rate. Every row for that site is kept: past days still price. Returns the full list.'
  },
  listTaxRates: {
    method: 'GET',
    path: '/api/admin/prices/taxes',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'TaxRateListResponse',
    description: 'Every GST and PST rate row, newest effective date first.'
  },
  createTaxRate: {
    method: 'POST',
    path: '/api/admin/prices/taxes',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'CreateTaxRateRequest',
    response: 'TaxRate',
    description: 'A new GST or PST percentage, effective from a date. Every price in the app is entered ex-tax.'
  },
  // No deleteTaxRate route (2026-08-28): GST and PST are two fixed kinds
  // that always exist, so a tax rate row cannot be taken away at all — only
  // superseded by a new effective-dated row. See the comment in
  // admin-prices.ts above the sales-tax routes.
  listEquipmentRates: {
    method: 'GET',
    path: '/api/admin/prices/equipment',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'EquipmentRateListResponse',
    description:
      'Every equipment charge rate row, archived included. What SCI bills per day for a named piece of equipment on site. A charge, not a cost.'
  },
  createEquipmentRate: {
    method: 'POST',
    path: '/api/admin/prices/equipment',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'CreateEquipmentRateRequest',
    response: 'EquipmentRate',
    description:
      'A new charge-out rate for a piece of equipment, effective from a date. charge_per_day is required; charge_per_week and charge_per_month are optional (null = not offered at that unit). A charge, not a cost.'
  },
  archiveEquipment: {
    method: 'POST',
    path: '/api/admin/prices/equipment/archive',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'ArchiveEquipmentRequest',
    response: 'EquipmentRateListResponse',
    description:
      'Removes a piece of equipment from the Rates list, or restores it. Every rate row of that equipment is kept: past days still bill. Returns the full list.'
  },
  listOperatorRates: {
    method: 'GET',
    path: '/api/admin/prices/operators',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OperatorRateListResponse',
    description:
      'Every operator charge-out rate row, archived included. What SCI bills per day for a named operator. A charge, not a cost — see worker_rate for what they are paid.'
  },
  createOperatorRate: {
    method: 'POST',
    path: '/api/admin/prices/operators',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'CreateOperatorRateRequest',
    response: 'OperatorRate',
    description:
      'A new $/day charge-out rate for a named operator, effective from a date. A charge, not a cost.'
  },
  archiveOperator: {
    method: 'POST',
    path: '/api/admin/prices/operators/archive',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'ArchiveOperatorRequest',
    response: 'OperatorRateListResponse',
    description:
      'Removes an operator from the Rates list, or restores them. Every rate row of that name is kept: past days still bill. Returns the full list.'
  },
  listCancellationFees: {
    method: 'GET',
    path: '/api/admin/prices/cancellation',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'CancellationFeeListResponse',
    description: 'Every cancellation fee row, newest effective date first. What SCI bills for a cancelled contract day.'
  },
  createCancellationFee: {
    method: 'POST',
    path: '/api/admin/prices/cancellation',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'CreateCancellationFeeRequest',
    response: 'CancellationFee',
    description: 'A new flat fee for a cancelled contract day, effective from a date.'
  },
  // No deleteCancellationFee route (2026-08-28): there is only ever one
  // cancellation fee to price with, so a row cannot be taken away at all —
  // only superseded by a new effective-dated row. See the comment in
  // admin-prices.ts above the cancellation-fee routes.

  /* --------------------------------------------------- admin: expenses */

  listExpenses: {
    method: 'GET',
    path: '/api/admin/expenses',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'ExpenseMonthResponse',
    description:
      'The expenses in one month (?month=YYYY-MM), every lease, and the overhead totals for that month.'
  },
  createExpense: {
    method: 'POST',
    path: '/api/admin/expenses',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'SaveExpenseRequest',
    response: 'Expense',
    description:
      'A spend, entered as the total printed on the statement. The server backs the GST out; it is never taken from the client.'
  },
  updateExpense: {
    method: 'PATCH',
    path: '/api/admin/expenses/:expenseId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'SaveExpenseRequest',
    response: 'Expense',
    description:
      'Edits one expense. Unlike a price, an expense IS editable: it records something that happened. The GST split is recomputed on every write.'
  },
  deleteExpense: {
    method: 'DELETE',
    path: '/api/admin/expenses/:expenseId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OkResponse',
    description: 'Removes one expense.'
  },
  listLeases: {
    method: 'GET',
    path: '/api/admin/leases',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'LeaseListResponse',
    description: 'Every standing monthly payment, running or ended.'
  },
  createLease: {
    method: 'POST',
    path: '/api/admin/leases',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'SaveLeaseRequest',
    response: 'EquipmentLease',
    description: 'A standing monthly payment, from a YYYY-MM and optionally until one.'
  },
  updateLease: {
    method: 'PATCH',
    path: '/api/admin/leases/:leaseId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'SaveLeaseRequest',
    response: 'EquipmentLease',
    description:
      'Edits one lease — including whether it counts toward the monthly total, which leaves it out of the Performance Dashboard for a month without deleting it.'
  },
  deleteLease: {
    method: 'DELETE',
    path: '/api/admin/leases/:leaseId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OkResponse',
    description: 'Removes one lease.'
  },
  listExpenseCategories: {
    method: 'GET',
    path: '/api/admin/expense-categories',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'ExpenseCategoryListResponse',
    description: 'Every expense category, archived ones included, in sort order.'
  },
  createExpenseCategory: {
    method: 'POST',
    path: '/api/admin/expense-categories',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'SaveExpenseCategoryRequest',
    response: 'ExpenseCategory',
    description: 'A new category. Names are unique, case-insensitively.'
  },
  updateExpenseCategory: {
    method: 'PATCH',
    path: '/api/admin/expense-categories/:categoryId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'SaveExpenseCategoryRequest',
    response: 'ExpenseCategory',
    description:
      'Renames a category, or archives it. There is no delete: an expense pointing at a deleted category would lose the only word saying what it was for.'
  },

  /* ----------------------------------------------------- admin: income */

  listIncome: {
    method: 'GET',
    path: '/api/admin/income',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'IncomeMonthResponse',
    description: 'The deposits in one month (?month=YYYY-MM), and that month’s income totals.'
  },
  createIncome: {
    method: 'POST',
    path: '/api/admin/income',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'SaveIncomeRequest',
    response: 'Income',
    description:
      'A deposit, entered as the total printed on the statement. The server backs the GST out; it is never taken from the client.'
  },
  updateIncome: {
    method: 'PATCH',
    path: '/api/admin/income/:incomeId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'SaveIncomeRequest',
    response: 'Income',
    description:
      'Edits one deposit. Like an expense, income IS editable: it records something that happened. The GST split is recomputed on every write.'
  },
  deleteIncome: {
    method: 'DELETE',
    path: '/api/admin/income/:incomeId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OkResponse',
    description: 'Removes one deposit.'
  },

  /* ------------------------------------------------- admin: statements */

  checkStatementLines: {
    method: 'POST',
    path: '/api/admin/statements/check',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'CheckStatementRequest',
    response: 'CheckStatementResponse',
    description:
      'Asks, before anything is written, which of these lines already exist (a debit against expense.fingerprint, a credit against income.fingerprint) and which look like a lease the owner has already entered. Writes nothing.'
  },
  importStatement: {
    method: 'POST',
    path: '/api/admin/statements',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: 'ImportStatementRequest',
    response: 'StatementImportResponse',
    description:
      'Commits the lines the owner accepted: a debit lands on expense, a credit on income, each optionally tagged to a job site, with the GST split computed server-side. Returns whether opening + credits − debits matches the closing balance, but never refuses the import over it — that is information for the owner, not a gate. The file itself is never sent or stored.'
  },
  listStatements: {
    method: 'GET',
    path: '/api/admin/statements',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'StatementListResponse',
    description: 'Every import, newest first, with how many of its expenses are still on the books.'
  },
  getStatement: {
    method: 'GET',
    path: '/api/admin/statements/:statementId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'StatementDetailResponse',
    description: 'One import and the expenses it brought in, whatever months they fell in.'
  },
  deleteStatement: {
    method: 'DELETE',
    path: '/api/admin/statements/:statementId',
    requiresRole: 'office',
    requiresArea: 'admin',
    request: null,
    response: 'OkResponse',
    description:
      'Removes the import RECORD. The expenses it brought in stay, with their link cleared — deleting a record of an import is not asking to lose a month of spending.'
  },

  /* ------------------------------------------------------------ projects */
  listProjects: {
    method: 'GET',
    path: '/api/projects',
    requiresRole: 'any',
    request: null,
    response: 'ProjectListResponse',
    description:
      'Projects visible to the current session. With no per-person accounts this is the session project alone.'
  },
  getProject: {
    method: 'GET',
    path: '/api/project',
    requiresRole: 'any',
    request: null,
    response: 'ProjectResponse',
    description: 'The session project, password hashes stripped.'
  },
  updateProject: {
    method: 'PATCH',
    path: '/api/project',
    requiresRole: 'crew',
    request: 'UpdateProjectRequest',
    response: 'ProjectResponse',
    description: 'Job defaults that never change: product, sci_unit_no, acceptance_point.'
  },

  /* ----------------------------------------------------------- job setup */
  getJobSetup: {
    method: 'GET',
    path: '/api/job-setup',
    requiresRole: 'any',
    request: null,
    response: 'JobSetupResponse',
    description: 'The one job setup for the session project, plus ITP items 1-5.'
  },
  updateJobSetup: {
    method: 'PATCH',
    path: '/api/job-setup',
    requiresRole: 'crew',
    request: 'UpdateJobSetupRequest',
    response: 'JobSetupResponse',
    description: 'Stable half of the Material/Water/Pump sheet.'
  },
  updateJobSetupItem: {
    method: 'PATCH',
    path: '/api/job-setup/items/:itemId',
    requiresRole: 'crew',
    request: 'UpdateJobSetupItemRequest',
    response: 'JobSetupItemResponse',
    description: 'ITP items 1-5. Server stamps initials from the shift name and sets recorded_at.'
  },

  /* --------------------------------------------------------------- pours */
  listPours: {
    method: 'GET',
    path: '/api/pours',
    requiresRole: 'any',
    request: null,
    response: 'PourListResponse',
    description: 'Pour list with completion counts, newest date first.'
  },
  createPour: {
    method: 'POST',
    path: '/api/pours',
    requiresRole: 'crew',
    request: 'CreatePourRequest',
    response: 'PourResponse',
    description:
      'Creates pour + header + material_loading + pre_pour_calibration + 3 calibration trials + ITP items 6-31 + the 14 end-of-pour items in ONE transaction. Optionally pre-fills from a previous pour.'
  },
  getPour: {
    method: 'GET',
    path: '/api/pours/:pourId',
    requiresRole: 'any',
    request: null,
    response: 'PourResponse',
    description: 'The whole pour record, every section and every repeating row.'
  },
  updatePour: {
    method: 'PATCH',
    path: '/api/pours/:pourId',
    requiresRole: 'crew',
    request: 'UpdatePourRequest',
    response: 'PourResponse',
    description:
      'Section-level flush. Send only the sections that changed. One audit entry per section changed. Out-of-range values are flagged, never rejected.'
  },
  closePour: {
    method: 'POST',
    path: '/api/pours/:pourId/close',
    requiresRole: 'crew',
    request: 'ClosePourRequest',
    response: 'ClosePourResponse',
    description:
      'Sets status=closed. 409 hold_point_open with blocking_items when a failed Hold point is unresolved and not paired with a stop-hold event. This is the ONLY hard block in the app.'
  },

  /* ---------------------------------------------------------- ITP items */
  updateItpItem: {
    method: 'PATCH',
    path: '/api/pours/:pourId/itp-items/:itemId',
    requiresRole: 'crew',
    request: 'UpdateItpItemRequest',
    response: 'ItpItemResponse',
    description:
      'ITP items 6-31. Returns hold_point_triggered=true when a Hold point has just been set to failed.'
  },

  /* --------------------------------------------------------- chute tests */
  listChuteTests: {
    method: 'GET',
    path: '/api/pours/:pourId/chute-tests',
    requiresRole: 'any',
    request: null,
    response: 'ChuteTestListResponse',
    description: 'Chute tests for a pour, ordered by seq.'
  },
  createChuteTest: {
    method: 'POST',
    path: '/api/pours/:pourId/chute-tests',
    requiresRole: 'crew',
    request: 'CreateChuteTestRequest',
    response: 'ChuteTestResponse',
    description:
      'Append a chute test row. Server assigns seq and computes wc_ratio = water_vol_l / grout_wt_kg. Append-only so two QC people never conflict.'
  },
  updateChuteTest: {
    method: 'PATCH',
    path: '/api/pours/:pourId/chute-tests/:testId',
    requiresRole: 'crew',
    request: 'UpdateChuteTestRequest',
    response: 'ChuteTestResponse',
    description: 'Recomputes wc_ratio on every write.'
  },
  deleteChuteTest: {
    method: 'DELETE',
    path: '/api/pours/:pourId/chute-tests/:testId',
    requiresRole: 'crew',
    request: null,
    response: 'OkResponse',
    description: 'Deletes the row and appends an audit entry holding its before-state.'
  },

  /* ---------------------------------------------------- stop-hold events */
  listStopHoldEvents: {
    method: 'GET',
    path: '/api/pours/:pourId/stop-hold-events',
    requiresRole: 'any',
    request: null,
    response: 'StopHoldEventListResponse',
    description: 'Stop-hold events for a pour, ordered by event_no.'
  },
  createStopHoldEvent: {
    method: 'POST',
    path: '/api/pours/:pourId/stop-hold-events',
    requiresRole: 'crew',
    request: 'CreateStopHoldEventRequest',
    response: 'StopHoldEventResponse',
    description: 'Server assigns event_no.'
  },
  updateStopHoldEvent: {
    method: 'PATCH',
    path: '/api/pours/:pourId/stop-hold-events/:eventId',
    requiresRole: 'crew',
    request: 'UpdateStopHoldEventRequest',
    response: 'StopHoldEventResponse',
    description: 'Record the corrective action and restart time.'
  },
  deleteStopHoldEvent: {
    method: 'DELETE',
    path: '/api/pours/:pourId/stop-hold-events/:eventId',
    requiresRole: 'crew',
    request: null,
    response: 'OkResponse',
    description: 'Deletes the row and appends an audit entry holding its before-state.'
  },

  /* --------------------------------------------------- end-of-pour items */
  listEndOfPourItems: {
    method: 'GET',
    path: '/api/pours/:pourId/end-of-pour-items',
    requiresRole: 'any',
    request: null,
    response: 'EndOfPourItemListResponse',
    description: 'The 14 Appendix B items for a pour, ordered by item_no.'
  },
  updateEndOfPourItem: {
    method: 'PATCH',
    path: '/api/pours/:pourId/end-of-pour-items/:itemId',
    requiresRole: 'crew',
    request: 'UpdateEndOfPourItemRequest',
    response: 'EndOfPourItemResponse',
    description: 'Yes / No / N-A plus notes.'
  },

  /* -------------------------------------------------------------- photos */
  listPhotos: {
    method: 'GET',
    path: '/api/pours/:pourId/photos',
    requiresRole: 'any',
    request: null,
    response: 'PhotoListResponse',
    description: 'Photos attached to a pour, newest first.'
  },
  photoUploadUrl: {
    method: 'POST',
    path: '/api/pours/:pourId/photos/upload-url',
    requiresRole: 'crew',
    request: 'PhotoUploadUrlRequest',
    response: 'PhotoUploadUrlResponse',
    description:
      'Short-lived signed URL the phone PUTs the downscaled JPEG straight to R2 with. Does not create a photo row.'
  },
  attachPhoto: {
    method: 'POST',
    path: '/api/pours/:pourId/photos',
    requiresRole: 'crew',
    request: 'AttachPhotoRequest',
    response: 'PhotoResponse',
    description:
      'Creates the photo row for an already-uploaded R2 key, optionally bound to a section + row_id. Retried by the client until it succeeds; the photo shows as pending until then.'
  },
  getPhotoContent: {
    method: 'GET',
    path: '/api/photos/:photoId/content',
    requiresRole: 'any',
    request: null,
    response: 'image/jpeg',
    description: 'Streams the R2 object. The only way the browser reads a photo; R2 is never public.'
  },

  /* -------------------------------------------------------------- export */
  exportPourCsv: {
    method: 'GET',
    path: '/api/pours/:pourId/export.csv',
    requiresRole: 'any',
    request: null,
    response: 'text/csv',
    description:
      'One CSV per pour. Chute tests and stop-hold events are emitted as their own tables under section title rows.'
  },
  exportPourXlsx: {
    method: 'GET',
    path: '/api/pours/:pourId/export.xlsx',
    requiresRole: 'any',
    request: null,
    response: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    description:
      'The same pour record as the CSV, as a real Excel workbook — with the photographs embedded in the sheet, which is the one thing a CSV cannot carry.'
  },
  exportProjectCsv: {
    method: 'GET',
    path: '/api/export/pours.csv',
    requiresRole: 'any',
    request: null,
    response: 'text/csv',
    description: 'All-pours-in-project roll-up.'
  },
  exportProjectXlsx: {
    method: 'GET',
    path: '/api/export/pours.xlsx',
    requiresRole: 'any',
    request: null,
    response: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    description:
      'The all-pours roll-up as an Excel workbook — the CSV roll-up plus one Photographs section per pour, embedded and clickable, within a documented size budget.'
  },
  exportPourPdf: {
    method: 'GET',
    path: '/api/pours/:pourId/export.pdf',
    requiresRole: 'any',
    request: null,
    response: 'application/pdf',
    description:
      'The pour record as a downloaded PDF file — the print-styled layout redrawn, so what a client receives is recognisably the same document they have today.'
  },

  /* -------------------------------------------------------- share tokens */
  listShareTokens: {
    method: 'GET',
    path: '/api/share-tokens',
    requiresRole: 'crew',
    request: null,
    response: 'ShareTokenListResponse',
    description: 'Issued client links, active and revoked.'
  },
  createShareToken: {
    method: 'POST',
    path: '/api/share-tokens',
    requiresRole: 'crew',
    request: 'CreateShareTokenRequest',
    response: 'ShareTokenResponse',
    description:
      'Issues a high-entropy revocable token. Regenerable per client, and never confusable with the crew password.'
  },
  revokeShareToken: {
    method: 'POST',
    path: '/api/share-tokens/:tokenId/revoke',
    requiresRole: 'crew',
    request: null,
    response: 'RevokeShareTokenResponse',
    description: 'Sets revoked_at. Sessions minted from the token stop working immediately.'
  },

  /* --------------------------------------------------------- audit trail */
  listAudit: {
    method: 'GET',
    path: '/api/audit',
    requiresRole: 'any',
    request: null,
    response: 'AuditListResponse',
    description:
      'Append-only audit log. Optional ?pour_id= and ?limit= query params. There is deliberately no route that writes, edits or deletes audit entries.'
  }
} as const satisfies Record<string, RouteDef>;

export type RouteName = keyof typeof API_ROUTES;

/**
 * Concrete URL builders. The frontend MUST use these rather than string
 * literals so a path change stays a one-file change.
 */
export const apiPath = {
  login: () => '/api/auth/login',
  shareLogin: () => '/api/auth/share-login',
  logout: () => '/api/auth/logout',
  session: () => '/api/session',
  setShift: () => '/api/session/shift',
  mixDesigns: () => '/api/mix-designs',
  mixDesign: (mixId: number) => `/api/mix-designs/${mixId}`,

  jhaRecords: () => '/api/quality/jha',
  jhaRecord: (jhaId: number) => `/api/quality/jha/${jhaId}`,

  fieldTestLogs: () => '/api/quality/field-tests',
  fieldTestLog: (fieldTestId: number) => `/api/quality/field-tests/${fieldTestId}`,

  equipmentInspections: () => '/api/quality/equipment',
  equipmentInspection: (equipmentId: number) => `/api/quality/equipment/${equipmentId}`,

  dailyReports: () => '/api/quality/daily-reports',
  dailyReport: (dailyReportId: number) => `/api/quality/daily-reports/${dailyReportId}`,
  dailyReportPhotos: (dailyReportId: number) => `/api/quality/daily-reports/${dailyReportId}/photos`,
  dailyReportPhoto: (photoId: number) => `/api/quality/daily-report-photos/${photoId}`,
  dailyReportPhotoContent: (photoId: number) => `/api/quality/daily-report-photos/${photoId}/content`,

  contractReports: () => '/api/quality/contract-reports',
  contractReport: (id: number) => `/api/quality/contract-reports/${id}`,
  equipmentOptions: () => '/api/quality/equipment-options',

  mixDesignOptions: () => '/api/quality/mix-design-options',
  materialOptions: () => '/api/quality/material-options',
  personnelOptions: () => '/api/quality/personnel-options',
  activityOptions: () => '/api/quality/activity-options',

  sites: () => '/api/sites',
  site: (siteId: number) => `/api/sites/${siteId}`,
  cementInventory: (siteId: number) => `/api/quality/cement?site=${siteId}`,
  cementCounts: () => '/api/quality/cement/counts',
  cementCountArchive: () => '/api/quality/cement/counts/archive',

  adminPnl: () => '/api/admin/pnl',
  adminPnlProject: () => '/api/admin/pnl/project',
  adminPnlMonths: () => '/api/admin/pnl/months',
  adminSites: () => '/api/admin/sites',
  adminBreakdown: () => '/api/admin/breakdown',
  adminRateGaps: () => '/api/admin/prices/gaps',
  adminDismissName: () => '/api/admin/prices/dismiss',
  materialPrices: () => '/api/admin/prices/materials',
  materialArchive: () => '/api/admin/prices/materials/archive',
  activityRates: () => '/api/admin/prices/activities',
  activityArchive: () => '/api/admin/prices/activities/archive',
  workerRates: () => '/api/admin/prices/workers',
  workerArchive: () => '/api/admin/prices/workers/archive',
  mixPrices: () => '/api/admin/prices/mixes',
  mixArchive: () => '/api/admin/prices/mixes/archive',
  truckingRates: () => '/api/admin/prices/trucking',
  truckingArchive: () => '/api/admin/prices/trucking/archive',
  taxRates: () => '/api/admin/prices/taxes',
  equipmentRates: () => '/api/admin/prices/equipment',
  equipmentArchive: () => '/api/admin/prices/equipment/archive',
  operatorRates: () => '/api/admin/prices/operators',
  operatorArchive: () => '/api/admin/prices/operators/archive',
  cancellationFees: () => '/api/admin/prices/cancellation',

  adminExpenses: () => '/api/admin/expenses',
  adminExpense: (expenseId: number) => `/api/admin/expenses/${expenseId}`,
  adminLeases: () => '/api/admin/leases',
  adminLease: (leaseId: number) => `/api/admin/leases/${leaseId}`,
  expenseCategories: () => '/api/admin/expense-categories',
  expenseCategory: (categoryId: number) => `/api/admin/expense-categories/${categoryId}`,

  adminIncome: () => '/api/admin/income',
  adminIncomeRecord: (incomeId: number) => `/api/admin/income/${incomeId}`,

  adminStatements: () => '/api/admin/statements',
  adminStatementCheck: () => '/api/admin/statements/check',
  adminStatement: (statementId: number) => `/api/admin/statements/${statementId}`,

  projects: () => '/api/projects',
  project: () => '/api/project',

  jobSetup: () => '/api/job-setup',
  jobSetupItem: (itemId: number) => `/api/job-setup/items/${itemId}`,

  pours: () => '/api/pours',
  pour: (pourId: number) => `/api/pours/${pourId}`,
  closePour: (pourId: number) => `/api/pours/${pourId}/close`,

  itpItem: (pourId: number, itemId: number) => `/api/pours/${pourId}/itp-items/${itemId}`,

  chuteTests: (pourId: number) => `/api/pours/${pourId}/chute-tests`,
  chuteTest: (pourId: number, testId: number) => `/api/pours/${pourId}/chute-tests/${testId}`,

  stopHoldEvents: (pourId: number) => `/api/pours/${pourId}/stop-hold-events`,
  stopHoldEvent: (pourId: number, eventId: number) =>
    `/api/pours/${pourId}/stop-hold-events/${eventId}`,

  endOfPourItems: (pourId: number) => `/api/pours/${pourId}/end-of-pour-items`,
  endOfPourItem: (pourId: number, itemId: number) =>
    `/api/pours/${pourId}/end-of-pour-items/${itemId}`,

  photos: (pourId: number) => `/api/pours/${pourId}/photos`,
  photoUploadUrl: (pourId: number) => `/api/pours/${pourId}/photos/upload-url`,
  photoContent: (photoId: number) => `/api/photos/${photoId}/content`,

  exportPourCsv: (pourId: number) => `/api/pours/${pourId}/export.csv`,
  exportPourXlsx: (pourId: number) => `/api/pours/${pourId}/export.xlsx`,
  exportProjectCsv: () => '/api/export/pours.csv',
  exportProjectXlsx: () => '/api/export/pours.xlsx',
  exportPourPdf: (pourId: number) => `/api/pours/${pourId}/export.pdf`,

  shareTokens: () => '/api/share-tokens',
  revokeShareToken: (tokenId: number) => `/api/share-tokens/${tokenId}/revoke`,

  audit: (query?: { pour_id?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (query?.pour_id !== undefined) params.set('pour_id', String(query.pour_id));
    if (query?.limit !== undefined) params.set('limit', String(query.limit));
    const qs = params.toString();
    return qs ? `/api/audit?${qs}` : '/api/audit';
  }
} as const;

/** Every route a client (read-only field QC) session is allowed to reach. */
export const CLIENT_ALLOWED_ROUTES: RouteName[] = (
  Object.keys(API_ROUTES) as RouteName[]
).filter((name) => {
  const required = API_ROUTES[name].requiresRole;
  return required === 'public' || required === 'any';
});

/** Every route that must reject a client session with 403. */
export const CREW_ONLY_ROUTES: RouteName[] = (Object.keys(API_ROUTES) as RouteName[]).filter(
  (name) => API_ROUTES[name].requiresRole === 'crew'
);

/**
 * Every route only an office session may reach — and, just as importantly,
 * every route a crew or client session must be refused.
 */
export const OFFICE_ONLY_ROUTES: RouteName[] = (Object.keys(API_ROUTES) as RouteName[]).filter(
  (name) => API_ROUTES[name].requiresRole === 'office'
);

/**
 * Office routes, grouped by the area they belong to. The natural shape for a
 * per-area account list or app-bar switch — and the shape that makes it
 * obvious a new area means a new key here, not a widened check somewhere.
 */
function routesInArea(area: AccountArea): RouteName[] {
  return (Object.keys(API_ROUTES) as RouteName[]).filter((name) =>
    routeAreas(API_ROUTES[name] as RouteDef).includes(area)
  );
}

export const AREA_ROUTES: Record<AccountArea, RouteName[]> = {
  mix_designs: routesInArea('mix_designs'),
  quality: routesInArea('quality'),
  admin: routesInArea('admin')
};

/**
 * Does `role` (carrying `area`, for an office session) satisfy `required`
 * (carrying `routeArea`, for a route that declares one)? The single source of
 * the access rule.
 *
 * Note that 'any' is not "any role" — it is the field QC read surface, which
 * an office session is deliberately outside of. The two worlds share a cookie
 * format and nothing else.
 *
 * `routeArea` is checked only when the route declares one: an office session
 * satisfies a plain `requiresRole: 'office'` route regardless of its area, but
 * a route that also sets `requiresArea` refuses every office session whose
 * `area` does not match — including office sessions for an area that does not
 * exist yet, which is exactly the property that keeps adding one safe.
 */
export function roleSatisfies(
  required: RequiredRole,
  role: Role | null,
  area?: AccountArea | null,
  routeArea?: AccountArea | readonly AccountArea[]
): boolean {
  if (required === 'public') return true;
  if (role === null) return false;
  if (required === 'office') {
    if (role !== 'office') return false;
    if (routeArea !== undefined) {
      const areas = Array.isArray(routeArea) ? routeArea : [routeArea];
      if (!area || !areas.includes(area)) return false;
    }
    return true;
  }
  if (role === 'office') return false;
  if (required === 'any') return true;
  return role === 'crew';
}
